import { Fragment, type ReactNode } from 'react';
import { Ship, Plane, Truck, Clock, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import type { QuoteOption } from '../types/trade.types';

/**
 * Las rutas posibles, una debajo de la otra (rediseño del 19-oct, artefacto
 * «Comercia · Rutas»).
 *
 * Antes iban en columnas, con el argumento de que apiladas no se comparan. El
 * artefacto las apila pero con el precio siempre en el mismo lugar —a la
 * derecha, en grande— y la diferencia contra la más económica al lado del
 * nombre: el ojo compara la columna de precios sin recordar nada. Y el recorrido
 * se ve entero (puerto de embarque → tránsito → destino), que en una columna
 * angosta no entraba.
 *
 * La elegida se abre ahí mismo con su desglose (`detalle`), en vez de abajo de
 * todas: lo que se está mirando queda pegado a la ruta que lo explica.
 */

const MODE_ICON: Record<string, typeof Ship> = { SEA: Ship, AIR: Plane, LAND: Truck };
const MODE_LABEL: Record<string, string> = { SEA: 'Marítimo', AIR: 'Aéreo', LAND: 'Terrestre' };
const MODE_TRAMO: Record<string, string> = { SEA: 'mar', AIR: 'aire', LAND: 'camión' };

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export interface QuoteComparisonProps {
  options: QuoteOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** El desglose de la elegida, que se abre dentro de su fila. */
  detalle?: ReactNode;
}

export function QuoteComparison({ options, selectedId, onSelect, detalle }: QuoteComparisonProps) {
  // La más económica por su precio, no por el orden: la lista se puede ordenar por tránsito.
  const cheapest = options.reduce<number | null>(
    (min, o) => (min === null ? o.totalPrice : Math.min(min, o.totalPrice)),
    null
  );
  const fastest = options.reduce<number | null>(
    (min, o) =>
      o.transitDaysMax === null
        ? min
        : min === null
          ? o.transitDaysMax
          : Math.min(min, o.transitDaysMax),
    null
  );

  return (
    <div className="space-y-3">
      {options.map((option) => (
        <OptionRow
          key={option.id}
          option={option}
          isSelected={selectedId === option.id}
          isCheapest={option.totalPrice === cheapest}
          isFastest={fastest !== null && option.transitDaysMax === fastest}
          delta={option.totalPrice - (cheapest ?? option.totalPrice)}
          onSelect={onSelect}
          detalle={selectedId === option.id ? detalle : null}
        />
      ))}
    </div>
  );
}

/** El recorrido de la ruta: cada puerto o ciudad, y entre ellos cómo se viaja. */
function Recorrido({ option }: { option: QuoteOption }) {
  const paradas = [option.legs[0]?.origin, ...option.legs.map((l) => l.destination)].filter(
    (p): p is string => Boolean(p)
  );
  if (paradas.length < 2) return null;
  return (
    <ol aria-label="Recorrido" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      {paradas.map((parada, i) => {
        const tramo = option.legs[i];
        const Icono = tramo ? (MODE_ICON[tramo.transportMode] ?? Truck) : null;
        return (
          <Fragment key={`${parada}-${i}`}>
            <li className="font-semibold">{parada}</li>
            {tramo && Icono && (
              <li
                aria-label={MODE_TRAMO[tramo.transportMode] ?? tramo.transportMode}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <span aria-hidden="true" className="h-px w-5 bg-border sm:w-8" />
                <Icono className="h-3.5 w-3.5" aria-hidden="true" />
                <span aria-hidden="true">{MODE_TRAMO[tramo.transportMode] ?? ''}</span>
                <span aria-hidden="true" className="h-px w-5 bg-border sm:w-8" />
              </li>
            )}
          </Fragment>
        );
      })}
    </ol>
  );
}

function OptionRow({
  option,
  isSelected,
  isCheapest,
  isFastest,
  delta,
  onSelect,
  detalle,
}: {
  option: QuoteOption;
  isSelected: boolean;
  isCheapest: boolean;
  isFastest: boolean;
  delta: number;
  onSelect: (id: string) => void;
  detalle: ReactNode;
}) {
  const Icon = MODE_ICON[option.transportMode] ?? Ship;
  const carriers = option.legs.map((l) => l.providerName);

  return (
    <article
      className={cn(
        'overflow-hidden rounded-tarjeta border bg-card shadow-sm transition-[border-color,box-shadow] duration-snap ease-snap',
        isSelected ? 'border-primary ring-1 ring-primary' : 'hover:border-foreground/20'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 p-5">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <Icon
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-label={MODE_LABEL[option.transportMode] ?? option.transportMode}
            />
            <h3 className="text-lg font-semibold leading-tight">
              {option.via ? `vía ${option.via}` : 'Directo'}
            </h3>
            <span
              className="min-w-0 truncate text-sm text-muted-foreground"
              title={carriers.join(' + ')}
            >
              {carriers.join(' + ')}
            </span>
            {option.transitDaysMin !== null && (
              <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="tabular-nums">
                  {option.transitDaysMin}–{option.transitDaysMax}
                </span>{' '}
                días de tránsito
              </span>
            )}
          </div>

          {/* Distintivos */}
          <div className="flex flex-wrap gap-1.5">
            {isCheapest && (
              <Badge tone="ok">
                <Check className="h-3 w-3" aria-hidden="true" />
                Más económica
              </Badge>
            )}
            {isFastest && !isCheapest && <Badge tone="info">Más rápida</Badge>}
            {!isCheapest && delta > 0 && (
              <Badge tone="neutro">+{money(delta, option.currency)} vs la más económica</Badge>
            )}
            {option.hasExpiredRates && (
              <Badge tone="alerta">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                Tarifa vencida
              </Badge>
            )}
          </div>

          <Recorrido option={option} />
        </div>

        {/* El precio, siempre en el mismo lugar para comparar de un vistazo. */}
        <div className="ml-auto text-right">
          {/* Costo y margen en chico: el vendedor necesita ver los dos para negociar. */}
          {option.totalMargin > 0 && (
            <dl className="mb-1 flex justify-end gap-3 text-xs text-muted-foreground">
              <div className="flex gap-1">
                <dt>costo</dt>
                <dd className="tabular-nums">{money(option.totalCost, option.currency)}</dd>
              </div>
              <div className="flex gap-1">
                <dt>margen</dt>
                <dd className="tabular-nums text-primary">
                  +{money(option.totalMargin, option.currency)}
                </dd>
              </div>
            </dl>
          )}
          <p className="text-xs text-muted-foreground">
            {option.totalMargin > 0 ? 'precio al cliente' : 'flete total del embarque'}
          </p>
          <p
            className={cn(
              'font-titulo text-[28px] font-semibold leading-tight tabular-nums',
              isCheapest && 'text-primary'
            )}
          >
            {money(option.totalPrice, option.currency)}
          </p>
        </div>

        <Button
          variant={isSelected ? 'degradado' : 'outline'}
          className="min-w-[112px]"
          aria-pressed={isSelected}
          onClick={() => onSelect(option.id)}
        >
          {isSelected ? (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              Elegida
            </>
          ) : (
            'Elegir'
          )}
        </Button>
      </div>

      {isSelected && detalle && <div className="border-t bg-muted/30 p-4 sm:p-5">{detalle}</div>}
    </article>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: 'ok' | 'info' | 'alerta' | 'neutro';
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
        tone === 'ok' && 'bg-ok-suave text-ok',
        tone === 'info' && 'bg-info-suave text-info',
        tone === 'alerta' && 'bg-alerta-suave text-alerta',
        tone === 'neutro' && 'bg-muted text-muted-foreground'
      )}
    >
      {children}
    </span>
  );
}
